import React, { FC } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { WhatsAppUserPreference } from './dto/WhatsAppUserPreference';
import { api } from '../functions/api';
import { ApiPath } from '../api/ApiPath';
import toast from 'react-hot-toast';
import { Customer } from '../customers/dto/Customer';

interface Props {
  preference: WhatsAppUserPreference;
  onClose: () => void;
  onUpdate: () => void;
  customers: Customer[];
  customersLoading: boolean;
}

interface FormData {
  defaultCustomerId: number | null;
}

export const WhatsAppPreferencesEdit: FC<Props> = ({ preference, onClose, onUpdate, customers, customersLoading }) => {
  const { t } = useTranslation();

  const { control, handleSubmit, formState } = useForm<FormData>({
    defaultValues: {
      defaultCustomerId: preference.defaultCustomerId,
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      await api({
        url: ApiPath.WhatsAppPreference.replace(':phoneNumber', encodeURIComponent(preference.phoneNumber)),
        method: 'PATCH',
        data: {
          defaultCustomerId: data.defaultCustomerId || null,
        },
      });
      toast.success(t('Preferences updated'));
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(t('Failed to update preferences'));
    }
  };

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>
          {t('Edit Preferences')} - {preference.phoneNumber.replace('whatsapp:', '')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Group className="mb-3">
            <Form.Label>{t('Default Customer')}</Form.Label>
            <Controller
              name="defaultCustomerId"
              control={control}
              render={({ field }) =>
                customersLoading ? (
                  <Form.Select disabled>
                    <option>{t('Loading...')}</option>
                  </Form.Select>
                ) : (
                  <Form.Select
                    {...field}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">{t('No default')}</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </Form.Select>
                )
              }
            />
            <Form.Text className="text-muted">
              {t('This customer will be used automatically when the user sends an allocation without specifying a customer.')}
            </Form.Text>
          </Form.Group>

          {preference.frequentCustomers.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>{t('Frequent Customers')}</Form.Label>
              <div className="text-muted small">
                {preference.frequentCustomers.map((c, i) => (
                  <div key={c.id}>
                    {i + 1}. {c.name} - {t('{{count}} uses', { count: c.useCount })}
                  </div>
                ))}
              </div>
            </Form.Group>
          )}

          <div className="d-flex justify-content-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={formState.isSubmitting}>
              {t('Save')}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </>
  );
};
